// Shared pure types and date-math helpers for the DevOps dashboard filter bar.
// Kept framework-free so both `DevopsDashboard.vue` (query building / URL sync)
// and `DevopsDashboardFilters.vue` (editing / validation) share one source of truth.

export type FilterScope = 'global' | 'org' | 'repo';

export type RangePreset =
  'last7d' |
  'last30d' |
  'last90d' |
  'thisMonth' |
  'thisQuarter' |
  'lastYear' |
  'custom';

export const RANGE_PRESETS: RangePreset[] = [
  'last7d',
  'last30d',
  'last90d',
  'thisMonth',
  'thisQuarter',
  'lastYear',
  'custom',
];

export type FilterValue = {
  orgId: number;
  orgName: string;
  teamId: number;
  teamName: string;
  repoId: number;
  repoName: string;
  userId: number;
  userName: string;
  rangePreset: RangePreset;
  since: string; // 'YYYY-MM-DD', always resolved to a concrete date
  until: string; // 'YYYY-MM-DD', always resolved to a concrete date
  top: number; // 0 means "all"
};

// Mirrors the shape of the `data-initial-filters` JSON attribute provided by the backend.
export type RawInitialFilters = {
  org_id: number;
  org_name: string;
  team_id: number;
  team_name: string;
  repo_id: number;
  repo_name: string;
  user_id: number;
  user_name: string;
  since: string;
  until: string;
  range_days: number;
  top: number;
};

export const DEFAULT_RAW_FILTERS: RawInitialFilters = {
  org_id: 0,
  org_name: '',
  team_id: 0,
  team_name: '',
  repo_id: 0,
  repo_name: '',
  user_id: 0,
  user_name: '',
  since: '',
  until: '',
  range_days: 30,
  top: 10,
};

function toDateOnly(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function trailingDaysRange(today: Date, days: number): {since: string; until: string} {
  const until = toDateOnly(today);
  return {since: toDateOnly(addDays(today, -(Math.max(days, 1) - 1))), until};
}

/**
 * Resolves a range preset into concrete since/until dates, always computed relative to
 * `today`. This is called both when applying filters (so the outgoing data request is
 * transparent/self-explanatory) and when restoring a bookmarked URL (so e.g. "last 7 days"
 * keeps meaning "the 7 days up to now", not a frozen historical range).
 */
export function resolvePresetRange(preset: RangePreset, today: Date): {since: string; until: string} {
  const until = toDateOnly(today);
  switch (preset) {
    case 'last7d': return trailingDaysRange(today, 7);
    case 'last30d': return trailingDaysRange(today, 30);
    case 'last90d': return trailingDaysRange(today, 90);
    case 'lastYear': return trailingDaysRange(today, 365);
    case 'thisMonth': return {since: toDateOnly(new Date(today.getFullYear(), today.getMonth(), 1)), until};
    case 'thisQuarter': {
      const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
      return {since: toDateOnly(new Date(today.getFullYear(), quarterStartMonth, 1)), until};
    }
    case 'custom': return {since: '', until: ''};
  }
}

/**
 * Resolves an arbitrary trailing-day window (e.g. an admin-configured `default_range_days` of
 * 14 or 60, which doesn't match any of the named quick presets) into concrete since/until
 * dates, using the exact same "N days ending today" math as the named trailing-day presets.
 */
export function resolveDaysRange(days: number, today: Date): {since: string; until: string} {
  return trailingDaysRange(today, days);
}

/**
 * Maps a day count to one of the named quick presets when it matches exactly, or 'custom'
 * otherwise. 'custom' is not a lossy fallback here: `buildFilterValueFromRaw` recomputes the
 * exact N-day window via `resolveDaysRange` for it, so odd configured values (e.g. 14 or 60
 * days) are preserved instead of silently becoming "last 30 days".
 */
function presetFromRangeDays(days: number): RangePreset {
  if (days === 7) return 'last7d';
  if (days === 30) return 'last30d';
  if (days === 90) return 'last90d';
  if (days === 365) return 'lastYear';
  return 'custom';
}

/** Builds a fully-resolved FilterValue (with concrete since/until) from the server-provided initial filters. */
export function buildFilterValueFromRaw(raw: RawInitialFilters, today: Date = new Date()): FilterValue {
  const value: FilterValue = {
    orgId: raw.org_id,
    orgName: raw.org_name,
    teamId: raw.team_id,
    teamName: raw.team_name,
    repoId: raw.repo_id,
    repoName: raw.repo_name,
    userId: raw.user_id,
    userName: raw.user_name,
    rangePreset: 'last30d',
    since: '',
    until: '',
    top: raw.top,
  };
  if (raw.since && raw.until) {
    value.rangePreset = 'custom';
    value.since = raw.since;
    value.until = raw.until;
  } else {
    const preset = presetFromRangeDays(raw.range_days);
    value.rangePreset = preset;
    const resolved = preset === 'custom' ?
      resolveDaysRange(raw.range_days, today) :
      resolvePresetRange(preset, today);
    value.since = resolved.since;
    value.until = resolved.until;
  }
  return value;
}

/** Start must not be after end, and the span must not exceed maxRangeDays (inclusive). */
export function isCustomRangeValid(since: string, until: string, maxRangeDays: number): boolean {
  if (!since || !until) return false;
  const sinceDate = new Date(`${since}T00:00:00`);
  const untilDate = new Date(`${until}T00:00:00`);
  if (Number.isNaN(sinceDate.getTime()) || Number.isNaN(untilDate.getTime())) return false;
  if (sinceDate.getTime() > untilDate.getTime()) return false;
  const spanDays = Math.round((untilDate.getTime() - sinceDate.getTime()) / 86400000) + 1;
  return spanDays <= maxRangeDays;
}

/**
 * Decides which filter selectors are visible for a given scope:
 * - "global" pages can filter by org, team, repo, and user.
 * - "org" pages have their org fixed by the route, so team, repo, and user remain.
 * - "repo" pages have everything fixed by the route; only the time range and top-N stay.
 */
export function visibleFilterFields(scopeType: FilterScope): {org: boolean; team: boolean; repo: boolean; user: boolean} {
  return {
    org: scopeType === 'global',
    team: scopeType !== 'repo',
    repo: scopeType !== 'repo',
    user: scopeType !== 'repo',
  };
}

function parseDefaultRangeDaysAttr(value: string | undefined): number {
  const match = /^(\d+)d$/.exec(value ?? '');
  return match ? Number(match[1]) : DEFAULT_RAW_FILTERS.range_days;
}

/**
 * Picks the reset-button / "filters active" baseline.
 *
 * `defaultFilters` (the `data-default-filters` attribute) reflects the admin-configured
 * defaults for this scope and never changes across requests, which is exactly what a reset
 * baseline needs. `initialFilters` (`data-initial-filters`), by contrast, reflects whatever
 * filters THIS particular request happened to resolve to (e.g. after applying `?top=20` from
 * the URL) - using it as the reset baseline would make "Reset" a no-op on any filtered/shared
 * URL, and would make the "filters active" indicator never light up after a reload.
 *
 * The backend always renders `data-default-filters` alongside `data-initial-filters` (see
 * `renderDevopsDashboard` in `ee/routers/web/dashboard/dashboard.go`), so this function should
 * normally never need to fall back. `initialFilters` is kept only as a last-resort defensive
 * fallback (e.g. a stale cached HTML response rendered by an older server build that predates
 * `data-default-filters`), so Reset degrades gracefully instead of throwing.
 */
export function resolveDefaultRawFilters(
  defaultFilters: RawInitialFilters | undefined,
  initialFilters: RawInitialFilters | undefined,
  defaultRangeAttr: string | undefined,
): RawInitialFilters {
  if (defaultFilters) return defaultFilters;
  if (initialFilters) return initialFilters;
  return {
    ...DEFAULT_RAW_FILTERS,
    range_days: parseDefaultRangeDaysAttr(defaultRangeAttr),
  };
}

/**
 * The TOP dropdown only offers a handful of preset counts (5/10/20/50/100/"all"), but the
 * admin-configured default can be any positive integer. If the current value isn't one of the
 * preset options, inject it so the <select> always has a matching (and thus visibly selected)
 * option instead of rendering blank. "All" (0) is always kept last since it reads as a
 * catch-all rather than a count.
 */
export function topSelectOptions(presetOptions: number[], currentValue: number): number[] {
  const options = new Set(presetOptions);
  options.add(currentValue);
  return Array.from(options).sort((a, b) => {
    if (a === 0) return 1;
    if (b === 0) return -1;
    return a - b;
  });
}

/**
 * Builds the exact set of query params written to the browser's address bar for a given filter
 * state. The preset is intentionally kept under a `preset` key, never `range`: the server's
 * `range` query parameter only accepts the legacy `Nd` format (e.g. "30d"), and it's also parsed
 * by the HTML page handler itself (to build the initial payload) - so writing a preset name like
 * "last30d" or "thisMonth" into `range` would make every reload/share/auto-refresh of the
 * resulting URL fail with a 400, instead of rendering the dashboard. `preset` is never read by
 * the server, so it's always safe to write.
 */
export function buildUrlSyncParams(filters: FilterValue, scopeType: FilterScope): Record<string, string> {
  const params: Record<string, string> = {};
  if (scopeType === 'global' && filters.orgId) {
    params.org = String(filters.orgId);
    params.org_name = filters.orgName;
  }
  if (scopeType !== 'repo' && filters.teamId) {
    params.team = String(filters.teamId);
    params.team_name = filters.teamName;
  }
  if (scopeType !== 'repo' && filters.repoId) {
    params.repo = String(filters.repoId);
    params.repo_name = filters.repoName;
  }
  if (scopeType !== 'repo' && filters.userId) {
    params.user = String(filters.userId);
    params.user_name = filters.userName;
  }
  params.preset = filters.rangePreset;
  if (filters.rangePreset === 'custom') {
    params.since = filters.since;
    params.until = filters.until;
  }
  params.top = String(filters.top);
  return params;
}

/**
 * Overlays the front-end-only `preset` URL parameter (see `buildUrlSyncParams` above) onto an
 * already server-resolved `FilterValue`, restoring the human-meaningful "last 7 days" / "this
 * quarter" selection that a plain page reload or a shared/bookmarked URL would otherwise lose.
 *
 * The server never reads `preset` at all: it only understands the legacy `range=Nd` form and,
 * for genuinely custom ranges, explicit `since`/`until` dates (see `applyDashboardRangeParams`
 * in `ee/routers/web/dashboard/dashboard.go`). So a URL like `?preset=last7d` round-trips
 * through the backend with the preset silently dropped, and the server-resolved `base` ends up
 * describing whatever range the scope's admin-configured default resolves to - not "last 7
 * days". Without this overlay, the filter bar would keep showing "last 7 days" (if seeded
 * elsewhere) while `/data` is actually queried for the default range, or the address bar would
 * get rewritten back to the default preset on load, silently discarding the user's selection.
 *
 * Only the three range fields (`rangePreset`/`since`/`until`) are ever overridden; every other
 * field on `base` - crucially the org/team/repo entity IDs, which are server-authorized and
 * sanitized - is returned completely untouched. That is deliberate: trusting a raw URL entity
 * ID here would reintroduce the exact bug this overlay's sibling fix addressed (a stale URL
 * pointing at an org/repo the viewer can no longer read must still degrade to the server's
 * safe fallback, never 403 the data request).
 *
 * `presetParam` is ignored (returning `base` unchanged) when it is missing, unrecognized, or
 * `'custom'`: a custom range's `since`/`until` are already read directly by the server, so
 * `base` already reflects them, and re-deriving here would be redundant at best (a no-op) and
 * destructive at worst (`resolvePresetRange('custom', ...)` returns blank dates, since it
 * expects the caller to already have the exact custom dates in hand).
 */
export function overlayUrlRangePreset(base: FilterValue, presetParam: string | null, today: Date): FilterValue {
  if (!presetParam || presetParam === 'custom' || !(RANGE_PRESETS as string[]).includes(presetParam)) {
    return base;
  }
  const preset = presetParam as RangePreset;
  const resolved = resolvePresetRange(preset, today);
  return {...base, rangePreset: preset, since: resolved.since, until: resolved.until};
}
